import type { TickLevel } from './types/tick-level'
import type { RulerParams } from './types/ruler-params'
import { getUnitConfig } from './helpers/ruler-unit-config'

export interface Tick {
  pos: number
  /**
   * Document-canvas coordinate (pt) along this axis for the tick grid line (`u` in `generateTicks`).
   * Stable across pan; use with `rulerTickReactKey` for list keys (not array index).
   */
  anchorU: number
  isMajor: boolean
  /** When set, use for three-level display: major = full size + label, mid = medium, minor = short. */
  level?: TickLevel
  label?: string
}

/** Stable React `key` for a ruler tick row (avoids float stringify noise). */
export function rulerTickReactKey(axis: 'h' | 'v', tick: Tick): string {
  const u = tick.anchorU

  if (!Number.isFinite(u)) {
    return `${axis}:invalid`
  }

  const normalized = Number(u.toPrecision(10))

  return `${axis}:${normalized}`
}

const MOD_EPS = 0.001

function isMultipleOf(value: number, interval: number): boolean {
  const r = value % interval

  return Math.abs(r) < MOD_EPS || Math.abs(r - interval) < MOD_EPS
}

/**
 * Generate ticks for a single axis (horizontal or vertical).
 * When horizontalZeroAtCanvasX is set, horizontal ruler shows 0 at that canvas X.
 * When verticalZeroAtCanvasY is set, vertical ruler shows 0 at that canvas Y (e.g. top of page content).
 */
function generateTicks(params: RulerParams, isHorizontal: boolean): Tick[] {
  const {
    unit,
    scale,
    originX,
    originY,
    canvasStartX,
    canvasEndX,
    canvasStartY,
    canvasEndY,
    horizontalZeroAtCanvasX,
    verticalZeroAtCanvasY,
  } = params
  const { minor, mid, major, label } = getUnitConfig(unit, scale)

  const origin = isHorizontal ? originX : originY
  const canvasStartPos = isHorizontal ? canvasStartX : canvasStartY
  const canvasEndPos = isHorizontal ? canvasEndX : canvasEndY

  const centerX = horizontalZeroAtCanvasX
  const zeroY = verticalZeroAtCanvasY

  // When vertical zero is set, align tick grid to zeroY so we hit 0, major, 2*major... and labels show
  let start: number
  let end: number

  if (isHorizontal && centerX !== undefined) {
    // Align tick grid to centerX so we hit 0, major, 2*major... and labels show
    start = centerX + Math.floor((canvasStartPos - centerX) / minor) * minor
    end = centerX + Math.ceil((canvasEndPos - centerX) / minor) * minor
  } else if (!isHorizontal && zeroY !== undefined) {
    start = zeroY + Math.floor((canvasStartPos - zeroY) / minor) * minor
    end = zeroY + Math.ceil((canvasEndPos - zeroY) / minor) * minor
  } else {
    start = Math.floor(canvasStartPos / minor) * minor
    end = Math.ceil(canvasEndPos / minor) * minor
  }

  const ticks: Tick[] = []

  for (let u = start; u <= end; u += minor) {
    const pos = origin + u * scale
    let rulerValue: number
    let level: TickLevel
    let showLabel: string | undefined

    if (isHorizontal && centerX !== undefined) {
      rulerValue = u < centerX ? centerX - u : u - centerX
      level =
        rulerValue === 0 || isMultipleOf(rulerValue, major)
          ? 'major'
          : isMultipleOf(rulerValue, mid)
            ? 'mid'
            : 'minor'
      showLabel = level === 'major' || rulerValue === 0 ? label(rulerValue) : undefined
    } else if (!isHorizontal && zeroY !== undefined) {
      // Same semantics as horizontal with center: distance from zero, labels on both sides
      rulerValue = u < zeroY ? zeroY - u : u - zeroY
      level =
        rulerValue === 0 || isMultipleOf(rulerValue, major)
          ? 'major'
          : isMultipleOf(rulerValue, mid)
            ? 'mid'
            : 'minor'
      showLabel = level === 'major' || rulerValue === 0 ? label(rulerValue) : undefined
    } else {
      rulerValue = u
      level = isMultipleOf(u, major) ? 'major' : isMultipleOf(u, mid) ? 'mid' : 'minor'
      showLabel = level === 'major' && u !== 0 ? label(u) : undefined
    }

    ticks.push({
      pos,
      anchorU: u,
      isMajor: level === 'major',
      level,
      label: showLabel,
    })
  }

  if (
    isHorizontal &&
    centerX !== undefined &&
    centerX >= canvasStartPos &&
    centerX <= canvasEndPos
  ) {
    const centerPos = origin + centerX * scale
    const hasCenterTick = ticks.some((t) => Math.abs(t.pos - centerPos) < 0.5)

    if (!hasCenterTick) {
      ticks.push({
        pos: centerPos,
        anchorU: centerX,
        isMajor: true,
        level: 'major',
        label: label(0),
      })
      ticks.sort((a, b) => a.pos - b.pos)
    }
  }

  if (!isHorizontal && zeroY !== undefined && zeroY >= canvasStartPos && zeroY <= canvasEndPos) {
    const zeroPos = origin + zeroY * scale
    const hasZeroTick = ticks.some((t) => Math.abs(t.pos - zeroPos) < 0.5)

    if (!hasZeroTick) {
      ticks.push({
        pos: zeroPos,
        anchorU: zeroY,
        isMajor: true,
        level: 'major',
        label: label(0),
      })
      ticks.sort((a, b) => a.pos - b.pos)
    }
  }

  return ticks
}

/**
 * Compute horizontal ruler ticks only
 * This function will be measured separately for performance analysis
 */
export function computeHorizontalRulerTicks(params: RulerParams): Tick[] {
  return generateTicks(params, true)
}

/**
 * Compute vertical ruler ticks only
 * This function will be measured separately for performance analysis
 */
export function computeVerticalRulerTicks(params: RulerParams): Tick[] {
  return generateTicks(params, false)
}
